import { OVERLAY_OPEN, OVERLAY_CLOSE } from './arctic-header-events.js';

class ArcticMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.openDelay = Number.parseInt(this.dataset.openDelay, 10) || 120;
    this.closeDelay = Number.parseInt(this.dataset.closeDelay, 10) || 120;
    this.openTimer = null;
    this.closeTimer = null;
    this.hideTimers = new WeakMap();
    this.activePanel = null;
    this.activeTrigger = null;
    this.pointerFocus = false;
    this.pointerOpenedTrigger = null;
    this.pointerOpenedAt = 0;
    this.suppressFocusOpen = false;
    this.closeTransitionMs = 180;
    this.hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-mega-panel]'));
    this.railLinks = Array.from(this.querySelectorAll('[data-mega-rail-target]'));
    this.navItems = Array.from(this.querySelectorAll('.mega-nav__trigger, .mega-nav__link'));

    this.triggers.forEach((trigger) => {
      if (this.hoverCapable) {
        trigger.addEventListener('pointerenter', this.onTriggerPointerEnter);
      }

      trigger.addEventListener('pointerdown', this.onTriggerPointerDown);
      trigger.addEventListener('click', this.onTriggerClick);
      trigger.addEventListener('focus', this.onTriggerFocus);
      trigger.addEventListener('keydown', this.onTriggerKeydown);
    });

    if (this.hoverCapable) {
      this.addEventListener('pointerleave', this.onRootPointerLeave);
      this.addEventListener('pointerenter', this.onRootPointerEnter);
    }

    this.railLinks.forEach((link) => {
      link.addEventListener('pointerenter', this.onRailIntent);
      link.addEventListener('focus', this.onRailIntent);
      link.addEventListener('keydown', this.onRailKeydown);
    });

    this.addEventListener('focusout', this.onFocusOut);
    document.addEventListener('keydown', this.onDocumentKeydown);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
  }

  disconnectedCallback() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.panels?.forEach((panel) => {
      const hideTimer = this.hideTimers.get(panel);
      if (hideTimer) clearTimeout(hideTimer);
    });
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
  }

  open(trigger, immediate = false) {
    const panel = this.getPanelForTrigger(trigger);
    if (!panel) return;

    clearTimeout(this.closeTimer);
    clearTimeout(this.openTimer);

    const run = () => {
      if (this.activePanel === panel) return;
      this.close({ restoreFocus: false });

      this.activePanel = panel;
      this.activeTrigger = trigger;
      this.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      this.clearPanelHideTimer(panel);
      panel.hidden = false;

      requestAnimationFrame(() => {
        if (this.activePanel !== panel || panel.hidden) return;
        panel.classList.add('is-open');
      });

      this.dispatchEvent(
        new CustomEvent(OVERLAY_OPEN, {
          bubbles: true,
          detail: { el: panel, source: 'mega-menu' },
        })
      );
    };

    if (immediate || !this.activePanel) {
      run();
      return;
    }

    this.openTimer = setTimeout(run, this.openDelay);
  }

  close(options = {}) {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);

    const panel = this.activePanel;
    const trigger = this.activeTrigger;
    if (!panel) return;

    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    this.activePanel = null;
    this.activeTrigger = null;
    this.pointerOpenedTrigger = null;
    this.pointerOpenedAt = 0;

    this.dispatchEvent(
      new CustomEvent(OVERLAY_CLOSE, {
        bubbles: true,
        detail: { el: panel, source: 'mega-menu' },
      })
    );

    if (options.restoreFocus && trigger instanceof HTMLElement) {
      this.suppressFocusOpen = true;
      trigger.focus({ preventScroll: true });
      requestAnimationFrame(() => {
        this.suppressFocusOpen = false;
      });
    }

    this.hidePanelAfterTransition(panel);
  }

  scheduleClose() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (this.matches(':hover')) return;
      this.close();
    }, this.closeDelay);
  }

  getPanelForTrigger(trigger) {
    const id = trigger?.getAttribute('aria-controls');
    if (!id) return null;
    return this.querySelector(`#${CSS.escape(id)}`);
  }

  activateRail(link) {
    const panel = link.closest('[data-mega-panel]');
    const id = link.dataset.megaRailTarget;
    if (!panel || !id) return;

    panel.querySelectorAll('[data-mega-rail-target]').forEach((item) => {
      item.classList.toggle('is-active', item === link);
    });

    panel.querySelectorAll('[data-mega-rail-panel]').forEach((group) => {
      const isActive = group.dataset.megaRailPanel === id;
      group.classList.toggle('is-active', isActive);
      group.toggleAttribute('inert', !isActive);
      group.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  clearPanelHideTimer(panel) {
    const hideTimer = this.hideTimers.get(panel);
    if (!hideTimer) return;
    clearTimeout(hideTimer);
    this.hideTimers.delete(panel);
  }

  hidePanelAfterTransition(panel) {
    this.clearPanelHideTimer(panel);
    const hideTimer = window.setTimeout(() => {
      if (!panel.classList.contains('is-open')) panel.hidden = true;
      this.hideTimers.delete(panel);
    }, this.closeTransitionMs);
    this.hideTimers.set(panel, hideTimer);
  }

  getPanelRailLinks(panel) {
    if (!panel) return [];
    return Array.from(panel.querySelectorAll('[data-mega-rail-target]'));
  }

  focusNavItem(current, direction) {
    const items = this.navItems.filter((item) => item.offsetParent !== null);
    const currentIndex = items.indexOf(current);
    if (currentIndex === -1) return;

    const nextIndex = (currentIndex + direction + items.length) % items.length;
    items[nextIndex]?.focus({ preventScroll: true });
  }

  focusPanelTile(panel) {
    const activeGroup = panel?.querySelector('[data-mega-rail-panel].is-active');
    const target = activeGroup?.querySelector('.mega-panel-tile') || activeGroup?.querySelector('a[href], button:not([disabled])');
    target?.focus({ preventScroll: true });
  }

  onTriggerPointerEnter = (event) => {
    this.pointerOpenedTrigger = event.currentTarget;
    this.pointerOpenedAt = performance.now();
    this.open(event.currentTarget);
  };

  onTriggerPointerDown = () => {
    this.pointerFocus = true;
    window.setTimeout(() => {
      this.pointerFocus = false;
    }, 0);
  };

  onTriggerFocus = (event) => {
    if (this.pointerFocus || this.suppressFocusOpen) return;
    this.open(event.currentTarget, true);
  };

  onTriggerClick = (event) => {
    event.preventDefault();
    const trigger = event.currentTarget;
    if (this.activeTrigger === trigger) {
      const openedFromThisPointer =
        this.hoverCapable &&
        this.pointerOpenedTrigger === trigger &&
        performance.now() - this.pointerOpenedAt < 500;

      if (openedFromThisPointer) return;

      this.close({ restoreFocus: true });
      return;
    }
    this.open(trigger, true);
  };

  onTriggerKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusNavItem(event.currentTarget, 1);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.focusNavItem(event.currentTarget, -1);
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const items = this.navItems.filter((item) => item.offsetParent !== null);
      const target = event.key === 'Home' ? items[0] : items[items.length - 1];
      target?.focus({ preventScroll: true });
      return;
    }

    if (event.key !== 'ArrowDown') return;

    event.preventDefault();
    const trigger = event.currentTarget;
    this.open(trigger, true);
    requestAnimationFrame(() => {
      const panel = this.getPanelForTrigger(trigger);
      const firstRail = panel?.querySelector('[data-mega-rail-target].is-active') || panel?.querySelector('[data-mega-rail-target]');
      firstRail?.focus({ preventScroll: true });
    });
  };

  onRootPointerLeave = () => {
    this.scheduleClose();
  };

  onRootPointerEnter = () => {
    clearTimeout(this.closeTimer);
  };

  onRailIntent = (event) => {
    this.activateRail(event.currentTarget);
  };

  onRailKeydown = (event) => {
    const link = event.currentTarget;
    const panel = link.closest('[data-mega-panel]');
    const links = this.getPanelRailLinks(panel);
    const currentIndex = links.indexOf(link);
    if (currentIndex === -1) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextLink = links[(currentIndex + direction + links.length) % links.length];
      nextLink?.focus({ preventScroll: true });
      if (nextLink) this.activateRail(nextLink);
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const nextLink = event.key === 'Home' ? links[0] : links[links.length - 1];
      nextLink?.focus({ preventScroll: true });
      if (nextLink) this.activateRail(nextLink);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.focusPanelTile(panel);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.activeTrigger?.focus({ preventScroll: true });
    }
  };

  onFocusOut = () => {
    requestAnimationFrame(() => {
      if (this.matches(':hover')) return;
      if (this.contains(document.activeElement)) return;
      this.close();
    });
  };

  onDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.activePanel) return;
    event.preventDefault();
    this.close({ restoreFocus: true });
  };

  onDocumentPointerDown = (event) => {
    if (!this.activePanel || this.contains(event.target)) return;
    this.close();
  };
}

if (!customElements.get('arctic-mega-menu')) {
  customElements.define('arctic-mega-menu', ArcticMegaMenu);
}
